const NO_OPTION_LABEL = '指定なし'
const BREAKDOWN_INDENT = '　' // 内訳の受領者行の字下げ（全角スペース1つ）

/**
 * オプション配列を表示用テキストに変換する
 * サーバー側(yonarezi PrintTicketService.options_text_for)と同じく、空白だけの項目は落とす
 * （残すと中身がある扱いになり、SDPとブラウザ直結印刷で伝票の形が変わるため）
 *
 * @param {Array<{group_name: string, option_name: string, value: *}>} [selectedOptions]
 * @returns {string}
 */
export function formatOptionsText(selectedOptions) {
  return (selectedOptions || [])
    .map(o => (o.value != null ? `${o.group_name}: ${o.value}` : o.option_name))
    .filter(s => s && s.trim())
    .join(' / ')
}

/**
 * キッチン伝票1枚分（プリンタ + 商品名）の受領者行と備考を組み立てる
 * サーバー側(yonarezi PrintTicketService.resolve_recipients_and_remark)と同じ規則に揃える
 *
 * - 中身(オプション)が1件も無ければ、受領者名を重複なしで並べるだけ（従来どおり）
 * - 中身があれば「中身 ×杯数」＋受領者(1人1行・重複なし)の内訳行にする。中身は備考に重ねて出さない
 * - slip-print は受領者行を1行ずつ出すだけなので、内訳行も recipientNames に入れる
 *
 * @param {Array<{names: string[], optionsText: string}>} orderEntries - 明細1件ごとの受領者名と中身
 * @param {string} [remark] - 商品の備考
 * @returns {{recipientNames: string[], itemRemark: (string|undefined)}}
 */
export function buildKitchenSlipLines(orderEntries, remark) {
  const itemRemark = remark || undefined

  if (orderEntries.every(e => !e.optionsText)) {
    return { recipientNames: [...new Set(orderEntries.flatMap(e => e.names))], itemRemark }
  }

  // Map は挿入順を保つので、中身・受領者とも注文に現れた順になる
  const byOption = new Map()
  orderEntries.forEach(e => {
    const label = e.optionsText || NO_OPTION_LABEL
    if (!byOption.has(label)) byOption.set(label, [])
    byOption.get(label).push(e)
  })

  const recipientNames = [...byOption].flatMap(([label, entries]) => {
    const names = [...new Set(entries.flatMap(e => e.names))]

    return [`${label} ×${entries.length}`, ...names.map(name => `${BREAKDOWN_INDENT}${name}`)]
  })

  return { recipientNames, itemRemark }
}

/**
 * スタッフ呼び出しのキッチン伝票1枚分の受領者行と備考を組み立てる（改修前の表示のまま）
 * SDP側(yonarezi PrintTicketService.enqueue_for_staff_call)もオプションを内訳にせず備考に出すため揃える
 *
 * - オプションが1種類（または無し）: 受領者名を重複なしで並べ、オプションは備考に出す
 * - オプションが複数種類: 「名前: オプション」の行にし、備考には出さない
 *
 * @param {Array<{names: string[], optionsText: string}>} orderEntries - 明細1件ごとの受領者名とオプション
 * @param {string} [remark] - 商品の備考
 * @returns {{recipientNames: string[], itemRemark: (string|undefined)}}
 */
export function buildStaffCallSlipLines(orderEntries, remark) {
  const recipientEntries = []
  orderEntries.forEach(({ names, optionsText }) => {
    names.forEach(name => {
      if (!recipientEntries.some(e => e.name === name && e.optionsText === optionsText)) {
        recipientEntries.push({ name, optionsText })
      }
    })
  })

  const uniqueOptionsTexts = [...new Set(recipientEntries.map(e => e.optionsText))]
  if (uniqueOptionsTexts.length <= 1) {
    return {
      recipientNames: recipientEntries.map(e => e.name),
      itemRemark: [uniqueOptionsTexts[0], remark].filter(Boolean).join(' | ') || undefined,
    }
  }

  return {
    recipientNames: recipientEntries.map(e => (e.optionsText ? `${e.name}: ${e.optionsText}` : e.name)),
    itemRemark: remark || undefined,
  }
}
